/**
 * jrs-cart-remove-position.js — remove ONE paid logo position from a customised cart line (window.JRS.CartRemovePosition).
 *
 * 2026-10-12 (Jim via Hamza): shoppers who reach checkout and find a Large Back or extra logo too dear had no way to
 * drop just that position on the cart page; Edit re-opens the whole customiser. Markup: snippets/logo-cart-item.liquid
 * renders [data-jrs-remove-position] on paid positions of lines that keep at least one logo.
 *
 * What a removal writes (two /cart/change.js calls, in this order):
 *   1. the position's decoration line -> quantity 0 (by its cart line key, re-checked against a fresh /cart.js);
 *   2. the garment (parent) line -> same quantity, properties rewritten so they describe the positions that remain:
 *      the removed slot's numbered properties dropped, later slots renumbered down by one (key spelling and order
 *      kept), _customization_count, _customization_data (positions, customizationCount, logoIds), _all_logo_ids
 *      and the slot-1 "_Logo Id" updated to match.
 *   No property is renamed or added: same keys order-alerts, the cart validator and the healer already read.
 *
 * Order matters: if step 2 fails, the parent still lists the removed position and the cart validator's heal re-adds
 * it on the next load, so the cart falls back to the original configuration instead of a mismatch. Children keep
 * their own _logoposition ordinal (informational only; order-alerts matches children by Position name).
 *
 * Refuses (and says so) rather than guessing when: the line changed since render, the slot cannot be matched in
 * _customization_data, or a child scopes itself by _ParentKey (legacy carts: a parent key change would orphan it).
 * Lines are matched by cart line key, _ParentVariant, _customization_instance_id and Position; never by _lineItemKey.
 *
 * Depends on: jrs-v2-cart-api.js (JRS.CartAPI). Cart page only (layout/theme.liquid Tier 3).
 */
(function (root) {
  'use strict';

  // Per-slot property bases on a customised parent (jrs-v2-cart-payload-builder.js addNumberedProperties plus the
  // pre-rename spellings the cart still dual-reads). Compared with one leading underscore stripped.
  var SLOT_BASES = {
    'Position': 1, 'Application Method': 1, 'Logo Type': 1, 'Logo Id': 1, 'Logo': 1, 'Logo Filename': 1,
    'Logo Text': 1, 'Logo Text 2': 1, 'Logo Text 3': 1, 'Logo Text 4': 1, 'Logo Color': 1, 'Logo Font': 1,
    'Note': 1, 'Applied To': 1, 'logoVariant': 1, 'logoproduct': 1, 'logoposition': 1
  };

  /** "Logo Id 2" -> { base: 'Logo Id', sep: ' ', n: 2 }; null when the key is not a per-slot key. */
  function parseSlotKey(key) {
    var m = /^(.+?)( ?)(\d+)$/.exec(String(key));
    if (!m) return null;
    var bare = m[1].charAt(0) === '_' ? m[1].slice(1) : m[1];
    if (!SLOT_BASES[bare]) return null;
    return { base: m[1], sep: m[2], n: Number(m[3]) };
  }

  /** First defined spelling of slot n's property (space form, no-space form, hidden form). */
  function slotValue(props, base, n) {
    var keys = [base + ' ' + n, base + n, '_' + base + ' ' + n, '_' + base + n];
    for (var i = 0; i < keys.length; i++) {
      if (typeof props[keys[i]] !== 'undefined' && props[keys[i]] !== null && props[keys[i]] !== '') return props[keys[i]];
    }
    return undefined;
  }

  function countSlots(props) {
    var n = 0;
    while (slotValue(props, 'Application Method', n + 1) !== undefined) n++;
    return n;
  }

  function lc(v) { return String(v == null ? '' : v).trim().toLowerCase(); }
  function numericOrString(v) { return /^\d+$/.test(String(v)) ? Number(v) : v; }

  /**
   * Pure: parent properties with slot n removed. Returns { ok, props, count } or { ok: false, reason }.
   * expectedName (optional) must match slot n's Position, case-insensitively.
   */
  function rewriteParentProperties(props, n, expectedName) {
    props = props || {};
    var count = countSlots(props);
    if (!(n >= 1 && n <= count)) return { ok: false, reason: 'slot_out_of_range' };
    if (count < 2) return { ok: false, reason: 'last_position' };
    var removedName = slotValue(props, 'Position', n);
    var removedLogoId = slotValue(props, 'Logo Id', n);
    if (expectedName && lc(removedName) !== lc(expectedName)) return { ok: false, reason: 'position_mismatch' };

    var out = {};
    Object.keys(props).forEach(function (key) {
      var slot = parseSlotKey(key);
      if (!slot || slot.n > count) { out[key] = props[key]; return; }
      if (slot.n === n) return;
      out[slot.n > n ? slot.base + slot.sep + (slot.n - 1) : key] = props[key];
    });

    var remaining = count - 1;
    if (typeof out._customization_count !== 'undefined') out._customization_count = String(remaining);

    var logoIds = [];
    for (var s = 1; s <= remaining; s++) {
      var id = slotValue(out, 'Logo Id', s);
      if (id !== undefined && logoIds.indexOf(String(id)) === -1) logoIds.push(String(id));
    }
    if (typeof out._all_logo_ids !== 'undefined') out._all_logo_ids = logoIds.join(',');
    if (typeof out['_Logo Id'] !== 'undefined' && logoIds.length) out['_Logo Id'] = slotValue(out, 'Logo Id', 1);

    if (typeof out._customization_data !== 'undefined' && out._customization_data !== '') {
      var cd;
      try { cd = typeof out._customization_data === 'string' ? JSON.parse(out._customization_data) : out._customization_data; }
      catch (e) { return { ok: false, reason: 'customization_data_unreadable' }; }
      var list = (cd && cd.positions) || [];
      var matches = function (p) { return p && lc(p.position) === lc(removedName) && String(p.logoId) === String(removedLogoId); };
      // Same index first (builder writes both lists in one order), else the one entry with that name + logo.
      var idx = matches(list[n - 1]) ? n - 1 : -1;
      if (idx === -1) {
        var found = [];
        for (var k = 0; k < list.length; k++) if (matches(list[k])) found.push(k);
        if (found.length !== 1) return { ok: false, reason: 'customization_data_mismatch' };
        idx = found[0];
      }
      list.splice(idx, 1);
      cd.positions = list;
      if (typeof cd.customizationCount !== 'undefined') cd.customizationCount = list.length;
      if (Array.isArray(cd.logoIds)) {
        var seen = {};
        cd.logoIds = [];
        list.forEach(function (p) { var k2 = String(p.logoId); if (!seen[k2]) { seen[k2] = 1; cd.logoIds.push(numericOrString(p.logoId)); } });
      }
      out._customization_data = JSON.stringify(cd);
    }

    return { ok: true, props: out, count: remaining, removed: { position: removedName, logoId: removedLogoId } };
  }

  /** The decoration line for this slot in a fresh cart: rendered key first, else one unambiguous match. */
  function findChild(items, parent, childKey, positionName) {
    var pv = String(parent.variant_id);
    var iid = (parent.properties || {})._customization_instance_id || '';
    function isChild(it) {
      var p = it.properties || {};
      if (String(p._ParentVariant || '') !== pv) return false;
      if (iid && p._customization_instance_id && p._customization_instance_id !== iid) return false;
      return lc(p.Position || p._Position) === lc(positionName);
    }
    for (var i = 0; i < items.length; i++) if (items[i].key === childKey && isChild(items[i])) return items[i];
    var c = items.filter(function (it) { return isChild(it) && Number(it.original_price || it.price) > 0; });
    return c.length === 1 ? c[0] : null;
  }

  // ---------------------------------------------------------------------------------------------------------------
  // DOM: inline confirm, then the two cart writes, then reload (same as cart-edit: line keys change).
  // ---------------------------------------------------------------------------------------------------------------

  function track(name, data) {
    try { (root.dataLayer = root.dataLayer || []).push(Object.assign({ event: name }, data)); } catch (e) {}
  }

  // The confirm and the error sit in their own <li> right after the logo row, so the row's flex layout is untouched.
  function sibling(row, cls) {
    var next = row.nextElementSibling;
    return next && next.classList.contains(cls) ? next : null;
  }

  function insertAfter(row, el) { row.parentNode.insertBefore(el, row.nextSibling); }

  function message(row, text) {
    var el = sibling(row, 'jrs-cart-remove-pos__msg');
    if (!el) {
      el = document.createElement('li');
      el.className = 'jrs-cart-remove-pos__msg';
      el.setAttribute('role', 'alert');
      insertAfter(row, el);
    }
    el.textContent = text;
  }

  function run(btn, row) {
    var CartAPI = root.JRS && root.JRS.CartAPI;
    var n = Number(btn.getAttribute('data-jrs-remove-position'));
    var parentKey = btn.getAttribute('data-jrs-parent-key');
    var childKey = btn.getAttribute('data-jrs-child-key');
    var name = btn.getAttribute('data-jrs-position-name');
    var info = { position: name, method: btn.getAttribute('data-jrs-position-method') || '' };
    var fail = function (reason) {
      track('jrs_cart_remove_position_failed', Object.assign({ reason: reason }, info));
      row.removeAttribute('aria-busy');
      var box = sibling(row, 'jrs-cart-remove-pos__confirm');
      if (box) box.parentNode.removeChild(box);
      btn.hidden = false;
      message(row, 'We couldn’t remove this logo here. Please use Edit, or refresh the page and try again.');
    };
    if (!CartAPI) return fail('no_cart_api');
    row.setAttribute('aria-busy', 'true');

    CartAPI.getCart().then(function (cart) {
      var items = (cart && cart.items) || [];
      var parent = null;
      for (var i = 0; i < items.length; i++) if (items[i].key === parentKey) parent = items[i];
      if (!parent) return fail('line_changed');
      var legacyScoped = items.some(function (it) { return it.properties && it.properties._ParentKey === parentKey; });
      if (legacyScoped) return fail('parent_key_scoped');
      var child = findChild(items, parent, childKey, name);
      if (!child) return fail('child_not_found');
      var rw = rewriteParentProperties(parent.properties, n, name);
      if (!rw.ok) return fail(rw.reason);

      return CartAPI.changeItem(child.key, { quantity: 0 }).then(function (r1) {
        if (!r1 || !r1.ok) return fail('child_remove_failed');
        return CartAPI.changeItem(parentKey, { quantity: parent.quantity, properties: rw.props }).then(function (r2) {
          // On a failed rewrite the reload's validator heal restores the removed logo (see header).
          track(r2 && r2.ok ? 'jrs_cart_remove_position' : 'jrs_cart_remove_position_failed',
            Object.assign({ reason: r2 && r2.ok ? '' : 'parent_rewrite_failed', price: Number(child.final_line_price || 0) / 100 }, info));
          root.location.reload();
        });
      });
    }).catch(function () { fail('cart_read_failed'); });
  }

  function confirmBox(btn) {
    var row = btn.closest('li') || btn.parentNode;
    if (sibling(row, 'jrs-cart-remove-pos__confirm')) return;
    var oldMsg = sibling(row, 'jrs-cart-remove-pos__msg');
    if (oldMsg) oldMsg.parentNode.removeChild(oldMsg);
    var name = btn.getAttribute('data-jrs-position-name') || 'this';
    var box = document.createElement('li');
    box.className = 'jrs-cart-remove-pos__confirm';
    box.setAttribute('role', 'group');
    box.setAttribute('aria-label', 'Remove ' + name + ' logo');
    var q = document.createElement('span');
    q.textContent = 'Remove the ' + name + ' logo from this item?';
    var yes = document.createElement('button');
    yes.type = 'button';
    yes.className = 'jrs-cart-remove-pos__yes';
    yes.textContent = 'Remove';
    var no = document.createElement('button');
    no.type = 'button';
    no.className = 'jrs-cart-remove-pos__no';
    no.textContent = 'Keep';
    box.appendChild(q); box.appendChild(yes); box.appendChild(no);
    insertAfter(row, box);
    btn.hidden = true;
    yes.focus();
    track('jrs_cart_remove_position_click', { position: name });
    no.addEventListener('click', function () { box.parentNode.removeChild(box); btn.hidden = false; btn.focus(); });
    yes.addEventListener('click', function () {
      yes.disabled = true; no.disabled = true;
      yes.textContent = 'Removing…';
      run(btn, row);
    });
  }

  var api = { rewriteParentProperties: rewriteParentProperties, findChild: findChild, parseSlotKey: parseSlotKey };

  if (typeof module !== 'undefined' && module.exports) { module.exports = api; return; }

  root.JRS = root.JRS || {};
  root.JRS.CartRemovePosition = api;
  // Delegated on document: the cart section re-render swaps the rows under it.
  if (typeof document === 'undefined') return;
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-jrs-remove-position]') : null;
    if (!btn) return;
    e.preventDefault();
    confirmBox(btn);
  });
})(typeof window !== 'undefined' ? window : this);
